import { ICalculation, ICalculationData, QuoteType } from "../../../../models/Lead";

type ApiCalculation = {
  Name?: string;
  IsFixed?: boolean;
  Value?: number;
  BaseValue?: number;
  IsAddition?: boolean;
  CustomCalculation?: string;
  DropOff?: boolean;
};

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const parseRawJson = (value: unknown) => {
  if (typeof value === "string") {
    if (value === "" || value === "{}") return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};

const parseNumber = (value: unknown) => {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return undefined;
};

const shouldIncludeCalculation = (calculation: ApiCalculation, leadIsCollection: boolean) => {
  if (typeof calculation?.DropOff !== "boolean") return true;
  return leadIsCollection ? !calculation.DropOff : calculation.DropOff;
};

const toCalculation = (calculation: ApiCalculation): ICalculation | null => {
  if (!calculation?.Name) return null;
  if ((calculation.Value == null || Number.isNaN(calculation.Value)) && !calculation.CustomCalculation) return null;

  return {
    name: calculation.Name,
    isFixed: calculation.IsFixed ?? true,
    value: calculation.Value ?? 0,
    baseValue: calculation.BaseValue ?? 0,
    isAddition: calculation.IsAddition ?? true,
    customCalculation: calculation.CustomCalculation ?? "",
  };
};

const normalizeApiCalculationArray = (data: unknown): ApiCalculation[] => {
  if (!Array.isArray(data)) return [];
  return data as ApiCalculation[];
};

const extractCalculations = (data: ApiCalculation[], leadIsCollection: boolean) => {
  const extractedCalculations: ICalculation[] = [];
  for (let i = 0; i < data.length; i++) {
    if (!shouldIncludeCalculation(data[i], leadIsCollection)) continue;
    const calculation = toCalculation(data[i]);
    if (!calculation) continue;
    extractedCalculations.push(calculation);
  }
  return extractedCalculations;
};

export const getQuoteTypeLabel = (quoteType: QuoteType) => {
  switch (quoteType) {
    case QuoteType.Salvage:
      return "Salvage";
    case QuoteType.ScrapExport:
      return "Scrap Export";
    case QuoteType.SmartPrice:
      return "Smart Price";
  }
};

export const parseCalculationData = (rawInput: unknown): ICalculationData | null => {
  const raw = parseRawJson(rawInput);
  if (!isRecord(raw)) return null;

  return {
    salvageCalculations: (raw.SalvageCalculations ?? []) as ICalculation[],
    tradePoorCalculations: (raw.TradePoorCalculations ?? []) as ICalculation[],
    postcodeCalculations: (raw.PostcodeCalculations ?? []) as ICalculation[],
    catalyticCalculations: (raw.CatalyticCalculations ?? []) as ICalculation[],
    engineCalculations: (raw.EngineCalculations ?? []) as ICalculation[],
    vfeCalculations: (raw.VfeCalculations ?? []) as ICalculation[],
    generalCalculations: (raw.GeneralCalculations ?? []) as ICalculation[],
    genericCalculations: (raw.GenericCalculations ?? []) as ICalculation[],
    additionalCalculations: (raw.AdditionalCalculations ?? []) as ICalculation[],
    partsLookupCalculations: (raw.PartsLookupCalculations ?? []) as ICalculation[],
    partsLookupGenericCalculations: (raw.PartsLookupGenericCalculations ?? []) as ICalculation[],
    dropOff: parseNumber(raw.DropOff) ?? 0,
    payment: (raw.Payment ?? {}) as ICalculation,
    collectionOption: (raw.CollectionOption ?? {}) as ICalculation,
    salvageVoucherCalculation: (raw.SalvageVoucherCalculation ?? {}) as ICalculation,
    scrapVoucherCalculation: (raw.ScrapVoucherCalculation ?? {}) as ICalculation,
  };
};

export const buildRowsForSide = (data: ICalculationData, quoteType: QuoteType, isCollection: boolean) => {
  const rows: ICalculation[] = [];

  switch (quoteType) {
    case QuoteType.Salvage:
      rows.push(
        ...extractCalculations(normalizeApiCalculationArray(data.tradePoorCalculations), isCollection),
        ...extractCalculations(normalizeApiCalculationArray(data.salvageCalculations), isCollection),
        ...extractCalculations([data.salvageVoucherCalculation as unknown as ApiCalculation], isCollection),
      );
      break;
    case QuoteType.ScrapExport:
      rows.push(
        ...extractCalculations(normalizeApiCalculationArray(data.postcodeCalculations), isCollection),
        ...extractCalculations(normalizeApiCalculationArray(data.catalyticCalculations), isCollection),
        ...extractCalculations(normalizeApiCalculationArray(data.engineCalculations), isCollection),
        ...extractCalculations(normalizeApiCalculationArray(data.vfeCalculations), isCollection),
        ...extractCalculations(normalizeApiCalculationArray(data.generalCalculations), isCollection),
        ...extractCalculations(normalizeApiCalculationArray(data.genericCalculations), isCollection),
        ...extractCalculations(normalizeApiCalculationArray(data.additionalCalculations), isCollection),
        ...extractCalculations([data.scrapVoucherCalculation as unknown as ApiCalculation], isCollection),
      );
      break;
    case QuoteType.SmartPrice:
      rows.push(
        ...extractCalculations(normalizeApiCalculationArray(data.partsLookupCalculations), isCollection),
        ...extractCalculations(normalizeApiCalculationArray(data.partsLookupGenericCalculations), isCollection),
        ...extractCalculations([data.scrapVoucherCalculation as unknown as ApiCalculation], isCollection),
      );
      break;
  }

  rows.push(...extractCalculations([data.payment as unknown as ApiCalculation], isCollection));
  rows.push(...extractCalculations([data.collectionOption as unknown as ApiCalculation], isCollection));
  return rows;
};

export const estimateTotalFromRows = (rows: ICalculation[]) => {
  let total = 0;

  for (let i = 0; i < rows.length; i++) {
    const calculation = rows[i];
    if (!calculation) continue;
    const amount = calculation.isFixed
      ? calculation.value
      : (calculation.baseValue * calculation.value) / 100;
    total += calculation.isAddition ? amount : -amount;
  }

  return Number.isFinite(total) ? total : null;
};
